import Search from '@mui/icons-material/Search'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import AccessTime from '@mui/icons-material/AccessTime'
import isEqual from 'lodash/isEqual'
import { CircularProgress, InputAdornment, TextField, Box, Typography, Chip, Button } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useNavigate } from 'react-router-dom'
import { getPaymentLinksForBookedEvents } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import { SentryRefHandler } from 'src/consumer/components/helperComponents'
import { getBookedSessionsForMentor } from 'src/consumer/services/featureServices'
import { prepareBookedEventDataForListing } from 'src/mentor/components/MentorEventsPage/components/utils'
import { MentorUpcomingPastEventsTable } from 'src/mentor/components/MentorEventsPage/MentorEventsComponentsAndHelpers'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMentorCommunicationTargetsBatch } from 'src/mentor/services/practitionerContactService'
import BookedEventLogsDrawer from 'src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer'
import BookedEventsFilters from './BookedEventsFilters'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { useContext } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { usePageCheckInGuard } from '../../hooks/usePageCheckInGuard'
import PageCheckInPrompt from '../PageCheckInPrompt'
import { getActivePeekIns, updatePeekInActivity, removePeekIn } from '../../utils/peekInManager'
import { peekOutFromRoom } from 'src/clinic/components/CheckIn/services'
import { toast } from 'react-toastify'
import { SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'

const SearchContainer = styled(Box)`
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`

const MentorPastEventsListing = forwardRef(
  (
    {
      listingApiSignature = '/api/secure/teacher/booked-slots/all/paginated',
      listOfPaymentLinksApiSignature,
      showMentorName = false,
      hideActions = false,
      hidePortalVoiceCallAction = false,
      sortingHandler,
      eventType,
      showOnlyPaidEvents = false,
      defaultFilters = undefined,
      logsApiEndpoint = '/api/secure/teacher/booked-event-info/logs/get',
      meetingSrcFilter, // 'aumhum' for video call, 'offline' for in-person
      showOnlyUnconfirmed = false, // Show only unconfirmed bookings
      filterByBookedStatus = true, // Filter by booked status (true = only booked, false = exclude booked, null = show all)
      hideSessionTypeFilter = false, // Hide Session Type filter
      eventTypeOptions,
      onRescheduleBooking = null,
      cancelAppointmentApiSignature = null,
      skipCancelCheckInGuard = false,
      onEngagementClick, // Callback for engagement settings (support portal)
      rightSideCustomComponent,
      bookingCardLayout = 'default',
      renderActionsHeaderTrailing,
      renderActionsSupplement,
      renderManageSessionActionItems,
      renderViewActionItems,
      renderBottomActionGroups,
    },
    componentRef,
  ) => {
    const normalizedDefaultFilters = useMemo(() => defaultFilters || {}, [defaultFilters])
    const [hasNextPage, setHasNextPage] = useState(true)
    const [pageNumber, setPageNumber] = useState(0)
    const [mentorBookedSlotsData, setMentorBookedSlotsData] = useState([])
    const [mentorBookedSlotsLoading, setMentorBookedSlotsLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [showBookedEventLogsDialog, setShowBookedEventLogsDialog] = useState(false)
    const [selectedBookedEvent, setSelectedBookedEvent] = useState(null)
    const [filters, setFilters] = useState(() => normalizedDefaultFilters)
    const [communicationTargetsByBookedEventId, setCommunicationTargetsByBookedEventId] = useState({})
    const [communicationTargetsLoading, setCommunicationTargetsLoading] = useState(false)
    const [checkedInDetails, setCheckedInDetails] = useState(null)
    const [peekIns, setPeekIns] = useState([])
    const mentorProfileContext = useContext(MentorProfileContext)
    const {
      mentorClinicAssociationData = null,
      mentorProfileData = null,
      portalViewSettings = {},
    } = mentorProfileContext || {}
    const hidePatientContactDetails = Boolean(portalViewSettings?.hidePatientContactDetails)

    // Page-level check-in guard
    const pageCheckInGuard = usePageCheckInGuard({ requireCheckIn: false })
    const {
      checkInPromptOpen: pageCheckInPromptOpen,
      checkingIn: pageCheckingIn,
      availableRooms: pageAvailableRooms,
      selectedRoom: pageSelectedRoom,
      selectedClinic: pageSelectedClinic,
      actionType: pageActionType,
      selectedCheckInMethod: pageSelectedCheckInMethod,
      isCheckedInOrPeekedIn: pageIsCheckedInOrPeekedIn,
      shouldBlockAccess,
      availableClinics: pageAvailableClinics,
      setCheckInPromptOpen: setPageCheckInPromptOpen,
      setSelectedRoom: setPageSelectedRoom,
      setActionType: setPageActionType,
      setSelectedCheckInMethod: setPageSelectedCheckInMethod,
      handleCheckIn: handlePageCheckIn,
      handlePeekIn: handlePagePeekIn,
      initializeCheckInPrompt: initializePageCheckInPrompt,
      handleClinicChange: handlePageClinicChange,
    } = pageCheckInGuard

    const [sentryRef] = useInfiniteScroll({
      hasNextPage,
      onLoadMore: getNextBookedSessionsPage,
      rootMargin: '0px 0px 20px 0px',
    })

    const navigate = useNavigate()

    // Debounce search query
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery)
      }, 500) // 500ms debounce delay

      return () => clearTimeout(timer)
    }, [searchQuery])

    // Build API params from filters
    const buildApiParams = useMemo(() => {
      const params = {
        page: 0,
        size: 20,
        sort: sortingHandler || 'startTime,DESC',
      }

      // Date range filters
      if (filters.startTime) {
        params.startTime = filters.startTime
      }
      if (filters.endTime) {
        params.endTime = filters.endTime
      }

      // Event types filter
      if (filters.eventTypes && filters.eventTypes.length > 0) {
        params.eventTypes = filters.eventTypes
      } else if (eventType) {
        params.eventTypes = Array.isArray(eventType) ? eventType : [eventType]
      }

      // Status filter
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.statusIds = filters.statusIds
      }

      // Payment status filter
      if (filters.paymentStatusIds && filters.paymentStatusIds.length > 0) {
        params.paymentStatusIds = filters.paymentStatusIds
      }

      // Blocked sessions flag
      if (filters.showBlockedSession !== undefined) {
        params.showBlockedSession = filters.showBlockedSession
      } else {
        params.showBlockedSession = !showOnlyPaidEvents
      }

      // Search query
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery
      }

      return params
    }, [filters, eventType, showOnlyPaidEvents, sortingHandler, debouncedSearchQuery])

    async function cleanExistingData() {
      setMentorBookedSlotsData([])
      setPageNumber(0)
      setHasNextPage(true)
      await setBookedSessionsFirstPage()
    }

    async function setBookedSessionsFirstPage() {
      setIsSearching(true)
      const params = { ...buildApiParams, page: 0 }
      const resp = await getBookedSessionsForMentor(0, listingApiSignature, params)
      if (resp?.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          let bookedEventData = prepareBookedEventDataForListing(respData)

          if (listOfPaymentLinksApiSignature) {
            const paymentLinks = await getPaymentLinksForBookedEvents(
              bookedEventData?.map((item) => item?.id),
              listOfPaymentLinksApiSignature,
            )
            bookedEventData = bookedEventData?.map((item) => {
              const paymentLink = paymentLinks?.filter(
                (paymentLink) => paymentLink?.bookedEventId === item?.id,
              )
              return {
                ...item,
                paymentLinks: paymentLink,
              }
            })
          }

          if (bookedEventData?.length <= 0) {
            setHasNextPage(false)
            setMentorBookedSlotsLoading(false)
            setIsSearching(false)
            return
          }
          console.log('bookedEventData', bookedEventData)
          setMentorBookedSlotsData(bookedEventData)
        }
      }
      setIsSearching(false)
    }

    async function getNextBookedSessionsPage() {
      setMentorBookedSlotsLoading(true)
      const nextPageNumber = pageNumber + 1
      const params = { ...buildApiParams, page: nextPageNumber }
      const resp = await getBookedSessionsForMentor(nextPageNumber, listingApiSignature, params)
      if (resp?.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          const bookedEventData = prepareBookedEventDataForListing(respData)
          if (bookedEventData?.length <= 0) {
            setHasNextPage(false)
            setMentorBookedSlotsLoading(false)
            return
          }
          setPageNumber(nextPageNumber)
          setMentorBookedSlotsData((prev) => [...prev, ...bookedEventData])
        }
      } else {
        setHasNextPage(false)
      }
      setMentorBookedSlotsLoading(false)
    }

    async function setup() {
      // Only show main loading on initial load, not on search
      if (!debouncedSearchQuery) {
        setMentorBookedSlotsLoading(true)
      }
      await cleanExistingData()
      if (!debouncedSearchQuery) {
        setMentorBookedSlotsLoading(false)
      }
    }

    useImperativeHandle(componentRef, () => ({
      resetData: cleanExistingData,
    }))

    function openBookedEventLogsDialog(bookedEvent) {
      setSelectedBookedEvent(bookedEvent)
      setShowBookedEventLogsDialog(true)
    }

    function closeBookedEventLogsDialog() {
      setShowBookedEventLogsDialog(false)
      setSelectedBookedEvent(null)
    }

    useEffect(() => {
      setup()
    }, [sortingHandler, debouncedSearchQuery, filters])

    useEffect(() => {
      setFilters((currentValue) =>
        isEqual(currentValue, normalizedDefaultFilters) ? currentValue : normalizedDefaultFilters,
      )
    }, [normalizedDefaultFilters])

    // Monitor checked-in status and peek-ins
    useEffect(() => {
      const updateStateIfChanged = (setter, nextValue) => {
        setter((currentValue) => (isEqual(currentValue, nextValue) ? currentValue : nextValue))
      }

      const updateStatus = () => {
        const checkedInData = localStorage.getItem('checkedInDetails')
        if (checkedInData) {
          try {
            const parsed = JSON.parse(checkedInData)
            updateStateIfChanged(setCheckedInDetails, parsed)
          } catch (e) {
            setCheckedInDetails(null)
          }
        } else {
          setCheckedInDetails(null)
        }
        
        // Update peek-ins
        const activePeekIns = getActivePeekIns()
        updateStateIfChanged(setPeekIns, activePeekIns)
      }

      updateStatus()

      // Listen for check-in/checkout events
      const handleCheckIn = () => updateStatus()
      const handleCheckOut = () => {
        setCheckedInDetails(null)
        updateStatus()
      }

      window.addEventListener('userCheckedIn', handleCheckIn)
      window.addEventListener('userCheckedOut', handleCheckOut)

      // Update every 10 seconds to check for expired peek-ins
      const interval = setInterval(updateStatus, 10000)

      return () => {
        window.removeEventListener('userCheckedIn', handleCheckIn)
        window.removeEventListener('userCheckedOut', handleCheckOut)
        clearInterval(interval)
      }
    }, [])

    // Track user activity to reset peek-in expiration (debounced)
    useEffect(() => {
      let activityTimeout = null

      const updatePeekInsIfChanged = (nextPeekIns) => {
        setPeekIns((currentValue) => (isEqual(currentValue, nextPeekIns) ? currentValue : nextPeekIns))
      }
      
      const handleActivity = () => {
        // Debounce activity updates (update every 30 seconds max)
        if (activityTimeout) {
          clearTimeout(activityTimeout)
        }
        
        activityTimeout = setTimeout(() => {
          // Update activity for all active peek-ins
          peekIns.forEach((peekIn) => {
            if (peekIn.clinicLocationId) {
              updatePeekInActivity(peekIn.clinicLocationId)
            }
          })
          // Refresh peek-ins after update
          const updated = getActivePeekIns()
          updatePeekInsIfChanged(updated)
        }, 30000) // Update every 30 seconds
      }

      // Listen for user activity events
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
      events.forEach((event) => {
        window.addEventListener(event, handleActivity, { passive: true })
      })

      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, handleActivity)
        })
        if (activityTimeout) {
          clearTimeout(activityTimeout)
        }
      }
    }, [peekIns])

    // Removed automatic check-in prompt opening
    // Check-in should only be required when user performs an action that requires it,
    // not just when viewing the page

    const handleFiltersChange = (newFilters) => {
      setFilters(newFilters)
    }

    const handleFiltersReset = () => {
      setFilters({})
    }

    const filteredBookedEvents = useMemo(
      () =>
        mentorBookedSlotsData.filter((event) => {
          if (filterByBookedStatus !== null && filterByBookedStatus !== undefined) {
            const isBooked = event?.status === BOOKING_STATUSES.BOOKED
            if (filterByBookedStatus && !isBooked) {
              return false
            }
            if (filterByBookedStatus === false && isBooked) {
              return false
            }
          }

          if (meetingSrcFilter && event?.meetingSrc !== meetingSrcFilter) {
            return false
          }

          if (showOnlyUnconfirmed) {
            const isUnconfirmed =
              event?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
              event?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
            if (!isUnconfirmed) {
              return false
            }
          }

          return true
        }),
      [filterByBookedStatus, meetingSrcFilter, mentorBookedSlotsData, showOnlyUnconfirmed],
    )

    const filteredBookedEventIdsKey = useMemo(
      () => filteredBookedEvents.map((event) => event?.id).filter(Boolean).join(','),
      [filteredBookedEvents],
    )

    useEffect(() => {
      let isCancelled = false

      async function loadCommunicationTargets() {
        if (!filteredBookedEventIdsKey) {
          setCommunicationTargetsLoading(false)
          return
        }

        setCommunicationTargetsLoading(true)
        const result = await getMentorCommunicationTargetsBatch(
          filteredBookedEvents.map((event) => event?.id).filter(Boolean),
        )

        if (isCancelled) {
          return
        }

        if (result?.ok && result?.targetsByBookedEventId) {
          setCommunicationTargetsByBookedEventId((currentValue) => ({
            ...currentValue,
            ...result.targetsByBookedEventId,
          }))
        }

        setCommunicationTargetsLoading(false)
      }

      void loadCommunicationTargets()

      return () => {
        isCancelled = true
      }
    }, [filteredBookedEventIdsKey, filteredBookedEvents])

    // Get clinic location and brand info for checked-in status
    const checkedInClinicLocation = useMemo(() => {
      if (!checkedInDetails?.clinicLocationId || !mentorClinicAssociationData) return null
      return mentorClinicAssociationData.find((clinic) => clinic.id === checkedInDetails.clinicLocationId)
    }, [checkedInDetails?.clinicLocationId, mentorClinicAssociationData])

    const checkedInClinicBrand = useMemo(() => {
      if (!checkedInClinicLocation) return null
      // Find brand from clinic location
      return checkedInClinicLocation.clinicBrandName || checkedInClinicLocation.clinicBrandData?.title || null
    }, [checkedInClinicLocation])

    // Get peek-in info for display (earliest expiring)
    const peekInInfo = useMemo(() => {
      if (peekIns.length === 0) return null
      
      // Get the earliest expiring peek-in
      const sorted = [...peekIns].sort((a, b) => {
        const aExp = dayjs(a.expiresAt)
        const bExp = dayjs(b.expiresAt)
        return aExp.diff(bExp)
      })
      
      return sorted[0]
    }, [peekIns])

    // Update expiration display every minute and clean expired peek-ins
    const [expirationUpdateTime, setExpirationUpdateTime] = useState(Date.now())
    useEffect(() => {
      if (peekIns.length > 0) {
        const interval = setInterval(() => {
          setExpirationUpdateTime(Date.now())
          // Refresh peek-ins to remove expired ones
          const updated = getActivePeekIns()
          setPeekIns((currentValue) => (isEqual(currentValue, updated) ? currentValue : updated))
        }, 60000) // Every minute

        return () => clearInterval(interval)
      }
    }, [peekIns.length])

    return (
      <>
        {/* Page-level Check-In Prompt */}
        <PageCheckInPrompt
          open={pageCheckInPromptOpen && !pageIsCheckedInOrPeekedIn}
          onClose={() => {
            // Don't allow closing if not checked in (optional: can be changed)
            // setPageCheckInPromptOpen(false)
          }}
          onCheckIn={handlePageCheckIn}
          onPeekIn={handlePagePeekIn}
          checkingIn={pageCheckingIn}
          availableRooms={pageAvailableRooms}
          selectedRoom={pageSelectedRoom}
          onRoomChange={setPageSelectedRoom}
          selectedClinic={pageSelectedClinic}
          onClinicChange={handlePageClinicChange}
          availableClinics={pageAvailableClinics}
          mentorProfileData={mentorProfileData}
          isCheckedInOrPeekedIn={pageIsCheckedInOrPeekedIn}
          actionType={pageActionType}
          onActionTypeChange={setPageActionType}
          selectedCheckInMethod={pageSelectedCheckInMethod}
          onCheckInMethodChange={setPageSelectedCheckInMethod}
        />

        {/* Overlay removed - Drawer component already provides its own backdrop */}

        {/* Content - hidden when check-in prompt is open and user is not checked in */}
        <Box
          sx={{
            opacity: pageCheckInPromptOpen && !pageIsCheckedInOrPeekedIn ? 0.3 : 1,
            pointerEvents: pageCheckInPromptOpen && !pageIsCheckedInOrPeekedIn ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
          }}
        >
          {/* Checked-In Status Strip */}
        {(checkedInDetails || peekIns.length > 0) && (
          <Box
            sx={{
              width: '100%',
              background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(116, 80, 170, 0.2)',
              boxShadow: '0 4px 20px rgba(116, 80, 170, 0.1)',
              py: 2.5,
              px: { xs: 2, sm: 3 },
              position: 'sticky',
              top: 0,
              zIndex: 100,
              mb: 2,
            }}
          >
            <Box
              sx={{
                maxWidth: '1400px',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.2) 0%, rgba(116, 80, 170, 0.1) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(116, 80, 170, 0.3)',
                    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.15)',
                    flexShrink: 0,
                  }}
                >
                  <MeetingRoom
                    sx={{
                      fontSize: { xs: 24, sm: 28 },
                      color: 'rgba(116, 80, 170, 1)',
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {checkedInDetails ? (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'rgba(15, 23, 42, 0.95)',
                          fontSize: { xs: '16px', sm: '18px', md: '20px' },
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Checked In: {checkedInDetails?.roomName || 'Room'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {checkedInClinicBrand && (
                          <Chip
                            label={checkedInClinicBrand}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(116, 80, 170, 0.15)',
                              color: 'rgba(116, 80, 170, 0.95)',
                              fontWeight: 600,
                              fontSize: '11px',
                              height: '22px',
                              border: '1px solid rgba(116, 80, 170, 0.3)',
                            }}
                          />
                        )}
                        {checkedInClinicLocation && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.9)',
                              fontSize: { xs: '12px', sm: '13px' },
                              fontWeight: 500,
                            }}
                          >
                            {checkedInClinicLocation.name}
                          </Typography>
                        )}
                        {checkedInDetails?.createdTime && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.7)',
                              fontSize: { xs: '11px', sm: '12px' },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <AccessTime sx={{ fontSize: '14px' }} />
                            {dayjs.utc(checkedInDetails.createdTime).local().format('h:mm A')}
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : peekIns.length > 0 ? (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'rgba(15, 23, 42, 0.95)',
                          fontSize: { xs: '16px', sm: '18px', md: '20px' },
                          mb: 1,
                        }}
                      >
                        Peeked In
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {peekIns.map((peekIn) => {
                          const clinic = mentorClinicAssociationData?.find((c) => c.id === peekIn.clinicLocationId)
                          const clinicName = clinic?.name || clinic?.title || peekIn.title || 'Unknown Clinic'
                          const brandName = clinic?.clinicBrandName || clinic?.clinicBrandData?.title || 'Unknown Brand'
                          
                          const handleLeave = async () => {
                            try {
                              // Log peek-out event
                              await peekOutFromRoom({
                                id: peekIn.id,
                                clinicLocationId: peekIn.clinicLocationId,
                                clinicBrandId: peekIn.clinicBrandId,
                                selectedCheckinEventOption: peekIn.selectedCheckinEventOption,
                              })
                              
                              // Remove peek-in from local storage
                              removePeekIn(peekIn.clinicLocationId)
                              
                              // Update state
                              const updated = getActivePeekIns()
                              setPeekIns(updated)
                              
                              toast.success('Left peek-in mode')
                            } catch (error) {
                              console.error('Error leaving peek-in:', error)
                              toast.error('Failed to leave peek-in')
                            }
                          }
                          
                          return (
                            <Box
                              key={peekIn.clinicLocationId}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                p: 1,
                                bgcolor: 'rgba(99, 102, 241, 0.05)',
                                borderRadius: '8px',
                                border: '1px solid rgba(99, 102, 241, 0.2)',
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'rgba(15, 23, 42, 0.95)',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {brandName} - {clinicName}
                                </Typography>
                                {peekIn.expiresAt && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'rgba(99, 102, 241, 0.8)',
                                      fontSize: { xs: '11px', sm: '12px' },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      mt: 0.25,
                                    }}
                                    key={expirationUpdateTime}
                                  >
                                    <AccessTime sx={{ fontSize: '12px' }} />
                                    Expires at {dayjs(peekIn.expiresAt).local().format('h:mm A')}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleLeave}
                                sx={{
                                  minWidth: 'auto',
                                  px: { xs: 1.5, sm: 2 },
                                  py: 0.5,
                                  fontSize: { xs: '11px', sm: '12px' },
                                  textTransform: 'none',
                                  borderColor: 'rgba(239, 68, 68, 0.5)',
                                  color: '#ef4444',
                                  '&:hover': {
                                    borderColor: '#ef4444',
                                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                                  },
                                }}
                              >
                                Leave
                              </Button>
                            </Box>
                          )
                        })}
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <CheckoutButtonComponent
                  buttonColor="error"
                  styleProp={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: { xs: 2.5, sm: 3 },
                    py: 1,
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                  additionalFunctionAfterAction={() => {
                    setCheckedInDetails(null)
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
        <BookedEventsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleFiltersReset}
          hideSessionTypeFilter={hideSessionTypeFilter}
          eventTypeOptions={eventTypeOptions}
        />
        <SearchContainer>
          <TextField
            fullWidth
            placeholder={
              hidePatientContactDetails
                ? 'Search by patient name, patient number, event type, or clinic...'
                : 'Search by patient name, number, mobile, email, alternate contacts, event type, or clinic...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={20} /> : <Search />}
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </SearchContainer>
        <MentorUpcomingPastEventsTable
          allEvents={filteredBookedEvents}
          loadingSlots={mentorBookedSlotsLoading}
          mentorProfileData={mentorProfileData}
          onClickJoin={(item) => navigate(`/l/${item?.slug}`)}
          refreshEvents={() => {
            cleanExistingData()
          }}
          showMentorName={showMentorName}
          hideActions={hideActions}
          hidePortalVoiceCallAction={hidePortalVoiceCallAction}
          openBookedEventLogsDialog={openBookedEventLogsDialog}
          onRescheduleBooking={onRescheduleBooking}
          cancelAppointmentApiSignature={cancelAppointmentApiSignature}
          skipCancelCheckInGuard={skipCancelCheckInGuard}
          communicationTargetsByBookedEventId={communicationTargetsByBookedEventId}
          communicationTargetsLoading={communicationTargetsLoading}
          communicationVisibleTargetKeys={['patient', 'support']}
          rightSideCustomComponent={rightSideCustomComponent}
          onEngagementClick={onEngagementClick}
          hidePatientContactDetails={hidePatientContactDetails}
          bookingCardLayout={bookingCardLayout}
          renderActionsHeaderTrailing={renderActionsHeaderTrailing}
          renderActionsSupplement={renderActionsSupplement}
          renderManageSessionActionItems={renderManageSessionActionItems}
          renderViewActionItems={renderViewActionItems}
          renderBottomActionGroups={renderBottomActionGroups}
        />
        {mentorBookedSlotsLoading ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px 0px',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <SentryRefHandler
            hasNextPage={hasNextPage}
            sentryRef={sentryRef}
            loading={mentorBookedSlotsLoading}
          />
        )}
        </Box>
        {selectedBookedEvent && (
          <BookedEventLogsDrawer
            open={showBookedEventLogsDialog}
            onClose={closeBookedEventLogsDialog}
            bookedSlot={{
              ...selectedBookedEvent,
              id: selectedBookedEvent?.id,
              startTime: selectedBookedEvent?.startTime,
              endTime: selectedBookedEvent?.endTime,
              patientInfo: {
                name: selectedBookedEvent?.patientInfoData?.name || selectedBookedEvent?.userName,
                patientNumber: selectedBookedEvent?.patientInfoData?.patientNumber,
                gender: selectedBookedEvent?.patientInfoData?.gender,
                dateOfBirth: selectedBookedEvent?.patientInfoData?.dateOfBirth,
                mobile:
                  hidePatientContactDetails ||
                  selectedBookedEvent?.patientContactDetailsVisible === false
                    ? undefined
                    : selectedBookedEvent?.patientInfoData?.mobile,
              },
              mentorName: selectedBookedEvent?.mentorName,
              eventType: selectedBookedEvent?.eventType,
            }}
            apiEndpoint={logsApiEndpoint}
          />
        )}
      </>
    )
  },
)

export default MentorPastEventsListing
